import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NavigationActions, NavigationApiActions } from '@common/store/navigation/actions/navigation.actions';
import {
    NavigationGroup,
    NavigationGroupTypes,
    NavigationItem,
    NavigationItemTypes,
    NavigationSection,
    NavigationSectionTypes,
    NavigationState,
} from '@common/store/navigation/models/navigation.models';
import {
    selectCurrentApplication,
    selectCurrentItem,
    selectNavigationData,
    selectSystemGroup,
    selectSystemSection,
    selectSystemSingleViewStateItem,
} from '@common/store/navigation/selectors/navigation.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Path } from '@src/suite/common/data-models/complex-types/path';
import { RecordFactory } from '@src/suite/common/data-models/records/record-factory';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { SingleItemViewStateRecord } from '@src/suite/common/data-models/records/single-item-viewstate-record';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { ViewStateFilterSerializer, ViewStateOptionsSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { ServiceRecord } from '@src/suite/common/data-models/service/service-record';
import { NAVIGATION_STORAGE_KEY } from '@src/suite/common/enums/constants';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { NavigationService } from '@src/suite/common/services/navigation-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectCreateViewStateGroup, selectCreateViewStateSection } from '@src/suite/common/store/createviewstate/createviewstate.selectors';
import { selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { ViewStateActions, ViewStateApiActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { getLayoutOptions } from '@src/suite/common/utils/viewstate-utils';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { catchError, concatMap, from, map, mergeMap, of, switchMap, tap } from 'rxjs';

@Injectable()
export class NavigationEffects {
    visualStateService = inject(VisualStateService);

    updateNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.saveNavigation),
            concatMap(action => {
                return this.navigationService.updateNavigation(action.application, action.layout, action.team, this.getNavigationCopy(action.sections)).pipe(
                    map(res => NavigationApiActions.saveNavigationSuccess({ status: res })),
                    catchError(error => of(NavigationApiActions.saveNavigationError({ error: error })))
                );
            })
        );
    });

    loadTeamNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.loadTeamNavigationData),
            switchMap(action => {
                return this.navigationService.loadTeamNavigation(action.team).pipe(
                    map(res => {
                        return NavigationApiActions.loadTeamNavigationDataSuccess({ navigation: res });
                    }),
                    catchError(error => of(NavigationApiActions.loadTeamNavigationDataError({ error: error })))
                );
            })
        );
    });

    initializeTeamNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.loadTeamNavigationDataSuccess),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            map(([action, application]) => {
                const sections: Array<NavigationSection> = [];
                action.navigation[application].forEach(s => {
                    const sectionPath: string = s.Label.replace(/\W+/g, '_').toLowerCase();

                    let sectionOpened: boolean = true;
                    if (this.visualStateService.expanderSettingsMap()?.Navigation) {
                        sectionOpened =
                            this.visualStateService.expanderSettingsMap().Navigation[application + '-' + NavigationGroupTypes.Team + '-' + this.navigationService.currentPlaybookTeam + '-' + s.ID];
                        if (sectionOpened == null) sectionOpened = true;
                    }

                    const section = {
                        id: s.ID,
                        label: s.Label,
                        order: s.Order,
                        hidden: true,
                        enterprise: s.Enterprise,
                        opened: sectionOpened,
                        inEditMode: s.inEditMode,
                        isNewSection: s.isNewSection,
                        prevLabel: s.prevLabel,
                        prevOpened: s.prevOpened,
                        path: sectionPath,
                        items: [],
                    };

                    s.Items.forEach((i, idx) => {
                        try {
                            const serviceRecord: ServiceRecord = ServiceRecord.instance(i.ViewState);

                            const viewState: ViewStateRecord = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);

                            let label: string = 'Undefined';
                            let url: Array<string> = [];
                            if (section.id !== NavigationSectionTypes.System && section.id !== NavigationSectionTypes.Default) {
                                if (viewState && viewState.title) {
                                    const lbl = 'PSTWebView';
                                    const title = this.languageSettings.get(`${lbl}_${i.Path}_${viewState.title}`).includes(lbl)
                                        ? viewState.title
                                        : this.languageSettings.get(`${lbl}_${i.Path}_${viewState.title}`);

                                    label = title;
                                }
                                url = ['/PST', application, 'team', sectionPath, viewState.guid];
                            }

                            const item: NavigationItem = {
                                database: i.Database,
                                path: i.Path,
                                guid: i.Guid,
                                hidden: false,
                                label: label,
                                sectionPath: sectionPath,
                                groupPath: 'team',
                                sectionId: section.id,
                                viewState: viewState,
                                url: url,
                                // @TODO
                                default: false,
                                order: idx,
                                system: false,
                                enterprise: viewState.path.isEnterpriseView,
                            };

                            section.items.push(item);
                        } catch (e) {
                            console.log(e);
                        }
                    });

                    sections.push(section);
                });
                return NavigationActions.initializeTeamNavigationDataSuccess({ sections: sections });
            })
        );
    });

    saveCurrentNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.saveCurrentNavigationItem),
            concatMap(() => {
                return this.navigationService.saveCurrentNavigationItem().pipe(
                    map(res => {
                        return NavigationApiActions.updateNavigationItemSuccess({ vs: res.data });
                    }),
                    catchError(error => of(NavigationApiActions.updateNavigationItemError({ error: error })))
                );
            })
        );
    });

    addNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.addNavigationItem),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            concatMap(([action, state]) => {
                let group: NavigationGroup = null;
                let section: NavigationSection = null;
                let item: NavigationItem = null;
                const vs: ViewStateRecord = action.vs;
                group = state.navigation[state.application].find(g => g.id === action.group);
                if (group) {
                    section = group.items.find(s => s.id === action.section);
                    if (section) {
                        item = this.createNewNavigationItem(state.application, group, section, vs);
                    }
                }
                if (group && section && item && vs) {
                    return of(NavigationApiActions.addNavigationItemSuccess({ group: group, section: section, item: item, vs: vs }));
                } else {
                    return of(NavigationApiActions.addNavigationItemError({ error: 'Failed to create view state record' }));
                }
            })
        );
    });

    createNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.createNavigationItem),
            concatLatestFrom(() => [this.store.select(selectNavigationData), this.store.select(selectCreateViewStateGroup), this.store.select(selectCreateViewStateSection)]),
            concatMap(([action, state, group, section]) => {
                return this.navigationService.createViewStateRecord(group?.id, section?.id, action.vs).pipe(
                    map(vs => {
                        let grp: NavigationGroup = null;
                        let sc: NavigationSection = null;
                        let it: NavigationItem = null;

                        if (group === null || section === null) {
                            grp = state.navigation[state.application].find(g => g.id === NavigationGroupTypes.System);
                            if (grp) {
                                sc = grp.items.find(s => s.id === NavigationSectionTypes.System);
                                if (sc) {
                                    it = sc.items.find(i => i.guid === NavigationItemTypes.SYSTEM_NEW_VIEW_GUID);
                                    it = { ...it, label: vs.title, viewState: vs };
                                }
                            }
                        } else {
                            grp = state.navigation[state.application].find(g => g.id === group.id);
                            if (grp) {
                                sc = grp.items.find(s => s.id === section.id);
                                if (sc) {
                                    it = this.createNewNavigationItem(state.application, group, section, vs);
                                }
                            }
                        }

                        if (grp && sc && it && vs) {
                            return NavigationApiActions.createNavigationItemSuccess({ group: grp, section: sc, item: it, vs: vs });
                        } else {
                            return NavigationApiActions.createNavigationItemError({ error: 'Failed to create view state record' });
                        }
                    }),
                    catchError(error => {
                        console.log(error);
                        return of(NavigationApiActions.createNavigationItemError({ error: error }));
                    })
                );
            })
        );
    });

    updateNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.updateNavigationItem),
            concatMap(action => {
                return this.navigationService.updateViewStateRecord(action.vs).pipe(
                    map(vs => {
                        return NavigationApiActions.updateNavigationItemSuccess({ vs: vs });
                    }),
                    catchError(error => {
                        return of(NavigationApiActions.updateNavigationItemError({ error: error }));
                    })
                );
            })
        );
    });

    createNavigationItemSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.createNavigationItemSuccess, NavigationApiActions.addNavigationItemSuccess),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            concatMap(([action, state]) => {
                return from(this.router.navigate(action.item.url)).pipe(
                    map(_ => {
                        if (action.group.id > 0) {
                            return NavigationApiActions.saveNavigation({
                                application: state.application,
                                layout: this.navigationService.getLayout(action.group),
                                team: this.navigationService.getTeam(action.group),
                                sections: action.group.items,
                                section: action.section,
                            });
                        }

                        return ApplicationActions.noOpAction();
                    }),
                    catchError(error => {
                        console.log(error);
                        return of(NavigationApiActions.updateNavigationItemError({ error: error }));
                    })
                );
            })
        );
    });

    updateNavigationSection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.updateSection, NavigationActions.removeSection),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            concatMap(([action, state]) => {
                if (action.group.id > 0) {
                    return of(
                        NavigationApiActions.saveNavigation({
                            application: state.application,
                            layout: this.navigationService.getLayout(action.group),
                            team: this.navigationService.getTeam(action.group),
                            sections: action.group.items,
                            section: action.section,
                        })
                    );
                }
            })
        );
    });

    loadNavigationData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.loadNavigationData),
            map(() => {
                const value = localStorage.getItem(NAVIGATION_STORAGE_KEY);
                if (value) {
                    try {
                        const state = JSON.parse(value);
                        try {
                            if (state.item && state.item.viewState) {
                                const serviceRecord: ServiceRecord = ServiceRecord.instance(state.item.viewState);
                                state.item.viewState = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);
                            }
                        } catch (e) {
                            console.log(e);
                        }

                        ['cast', 'discover', 'journal', 'playbook', 'live'].forEach((application: string) => {
                            state.navigation[application].forEach((group: NavigationGroup) => {
                                group.items.forEach((section: NavigationSection) => {
                                    section.items.map(item => {
                                        if (item.viewState) {
                                            try {
                                                const serviceRecord: ServiceRecord = ServiceRecord.instance(item.viewState);
                                                item.viewState = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);
                                            } catch (e) {
                                                console.log(e);
                                            }
                                        }
                                        return item;
                                    });
                                });
                            });
                        });

                        return NavigationActions.loadNavigationDataSuccess({ state: state });
                    } catch (error) {
                        console.log(error);
                        localStorage.removeItem(NAVIGATION_STORAGE_KEY);
                    }
                }
                return NavigationActions.loadNavigationDataError();
            })
        );
    });

    initializeNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.initializeNavigationData),
            map(() => {
                if (localStorage.getItem('Navigation')) {
                    const nav = JSON.parse(localStorage.getItem('Navigation'));

                    const state: NavigationState = {
                        application: null,
                        group: null,
                        section: null,
                        item: null,
                        default: null,
                        navigation: {
                            cast: new Array<NavigationGroup>(),
                            discover: new Array<NavigationGroup>(),
                            journal: new Array<NavigationGroup>(),
                            playbook: new Array<NavigationGroup>(),
                            live: new Array<NavigationGroup>(),
                        },
                    };

                    for (const app in nav) {
                        // Create system group, system section and system items
                        const systemGroup = {
                            id: NavigationGroupTypes.System,
                            label: 'System',
                            order: -1,
                            opened: false,
                            items: [],
                            system: true,
                            team: false,
                            path: 'system',
                        };

                        const systemSection = {
                            id: NavigationSectionTypes.System,
                            label: 'System',
                            order: -2,
                            hidden: true,
                            enterprise: false,
                            opened: false,
                            inEditMode: false,
                            isNewSection: false,
                            prevLabel: 'System',
                            prevOpened: false,
                            path: 'system',
                            items: [],
                        };

                        systemSection.items.push(this.createSystemSearchNavigationItem(app));
                        systemSection.items.push(this.createSystemCurrentNavigationItem(app));
                        systemSection.items.push(this.createSystemNewNavigationItem(app));
                        systemSection.items.push(this.createSystemSingleItemNavigationItem(app));

                        systemGroup.items.push(systemSection);

                        state.navigation[app].push(systemGroup);

                        nav[app].forEach(g => {
                            const groupPath: string = g.Label.replace(/\W+/g, '_').toLowerCase();

                            if (g.ID >= 0) {
                                let groupOpened: boolean = true;
                                if (this.visualStateService.expanderSettingsMap()?.Navigation) {
                                    groupOpened = this.visualStateService.expanderSettingsMap().Navigation[app + '-' + g.ID];
                                }
                                const group = {
                                    id: g.ID,
                                    label: g.Label,
                                    order: g.Order,
                                    opened: groupOpened,
                                    items: [],
                                    system: g.ID < 0 ? true : false,
                                    team: g.ID === NavigationGroupTypes.Team,
                                    path: groupPath,
                                    default: g.DefaultGuid ? g.DefaultGuid : null,
                                };

                                g.Items.forEach(s => {
                                    const sectionPath: string = s.Label.replace(/\W+/g, '_').toLowerCase();

                                    let sectionOpened: boolean = true;
                                    if (this.visualStateService.expanderSettingsMap()?.Navigation) {
                                        sectionOpened = this.visualStateService.expanderSettingsMap().Navigation[app + '-' + g.ID + '-' + this.navigationService.currentPlaybookTeam + '-' + s.ID];
                                        if (sectionOpened == null) sectionOpened = true;
                                    }

                                    const section = {
                                        id: s.ID,
                                        label: s.Label,
                                        order: s.Order,
                                        hidden: true,
                                        enterprise: s.Enterprise,
                                        opened: sectionOpened,
                                        inEditMode: s.inEditMode,
                                        isNewSection: s.isNewSection,
                                        prevLabel: s.prevLabel,
                                        prevOpened: s.prevOpened,
                                        path: sectionPath,
                                        items: [],
                                    };

                                    s.Items.forEach((i, idx) => {
                                        try {
                                            const serviceRecord: ServiceRecord = ServiceRecord.instance(i.ViewState);
                                            const viewState: ViewStateRecord = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);

                                            let label: string = 'Undefined';
                                            let url: Array<string> = [];
                                            if (section.id !== NavigationSectionTypes.System && section.id !== NavigationSectionTypes.Default) {
                                                if (viewState && viewState.title) {
                                                    const lbl = 'PSTWebView';
                                                    const title = this.languageSettings.get(`${lbl}_${i.Path}_${viewState.title}`).includes(lbl)
                                                        ? viewState.title
                                                        : this.languageSettings.get(`${lbl}_${i.Path}_${viewState.title}`);

                                                    label = title;
                                                }
                                                url = ['/PST', app, groupPath, sectionPath, viewState.guid];
                                            }

                                            const item: NavigationItem = {
                                                database: i.Database,
                                                path: i.Path,
                                                guid: i.Guid,
                                                hidden: false,
                                                label: label,
                                                sectionPath: sectionPath,
                                                groupPath: groupPath,
                                                sectionId: section.id,
                                                viewState: viewState,
                                                url: url,

                                                default: false,
                                                order: idx,
                                                system: false,
                                                enterprise: viewState.path.isEnterpriseView,
                                            };

                                            section.items.push(item);
                                        } catch (e) {
                                            console.log(e);
                                        }
                                    });

                                    group.items.push(section);
                                });

                                state.navigation[app].push(group);
                            }
                        });
                    }

                    return NavigationActions.initializeNavigationDataSuccess({ state: state });
                }
                return NavigationActions.initializeNavigationDataError({ error: 'No navigation in local storage found.' });
            })
        );
    });

    navigateToDefaultItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.navigateToDefaultItem),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            mergeMap(([_, state]) => {
                if (state.item) {
                    this.router.navigate(state.item.url);
                } else if (state.default) {
                    this.router.navigate(state.default.url);
                } else {
                    const group: NavigationGroup = state.navigation['playbook'].find((grp: NavigationGroup) => grp.id > 0);
                    if (group && group.items && group.items.length > 0) {
                        const section: NavigationSection = group.items[0];

                        if (section && section.items && section.items.length > 0) {
                            const item: NavigationItem = section.items[0];
                            if (item) {
                                this.router.navigate(item.url);
                            }
                        }
                    }
                }
                return of(WireApiActions.stopPolling({ restart: false }));
            })
        );
    });

    navigateToItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.navigateToItem),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([action, state]) => {
                    let item: NavigationItem = null;
                    state.navigation[state.application].forEach((group: NavigationGroup) => {
                        group.items.forEach((section: NavigationSection) => {
                            section.items.forEach(i => {
                                if (i && i.viewState && i.viewState && i.viewState.guid === action.item.viewState.guid) {
                                    item = i;
                                }
                            });
                        });
                    });
                    if (item) {
                        this.router.navigate(item.url);
                    }
                })
            );
        },
        { dispatch: false }
    );

    enterSearchMode$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.enterSearchMode),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([_, state]) => {
                    const group: NavigationGroup = state.navigation[state.application].find((grp: NavigationGroup) => grp.id < 0);

                    if (group && group.items && group.items.length > 0) {
                        // Find system section
                        const section: NavigationSection = group.items[0];

                        if (section && section.items && section.items.length > 0) {
                            const item: NavigationItem = section.items.find(it => it.guid === NavigationItemTypes.SYSTEM_SEARCH_GUID);

                            if (item) {
                                this.router.navigate(item.url);
                            }
                        }
                    }
                })
            );
        },
        { dispatch: false }
    );

    loadSingleNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.loadSingleNavigationItem),
            concatLatestFrom(() => [this.store.select(selectSystemGroup), this.store.select(selectSystemSection), this.store.select(selectSystemSingleViewStateItem)]),
            concatMap(([action, group, section, item]) => {
                return [NavigationActions.setCurrentItem({ group: group.path, section: section.path, item: item.guid }), RecordHttpActions.loadSingleItem({ guid: action.guid })];
            })
        );
    });

    setSingleNavigationItemType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.setSingleNavigationItemData),
            concatLatestFrom(() => [this.store.select(selectI18N), this.store.select(selectPSTGroupPrivileges), this.store.select(selectSystemSingleViewStateItem)]),
            map(([action, i18n, PSTGroupPrivs, item]) => {
                const layout = getLayoutOptions(action.itemtype, i18n, PSTGroupPrivs);
                return ViewStateActions.setCurrentViewStateSuccess({ vs: item.viewState, layout });
            })
        );
    });

    loadNavigationItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.loadNavigationItem),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([_, state]) => {
                    if (state.item) {
                        this.router.navigate(state.item.url);
                    }
                })
            );
        },
        { dispatch: false }
    );

    setDefaultNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.setDefaultNavigationItem),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            concatMap(([action, application]) => {
                return from(this.navigationService.setDefaultNavigationItem(action.guid, action.layout, application, action.team)).pipe(
                    map(res => NavigationApiActions.setDefaultNavigationItemSuccess({ status: !!res.data, group: action.group, guid: action.guid })),
                    catchError(error => of(NavigationApiActions.setDefaultNavigationItemError({ error: error })))
                );
            })
        );
    });

    removeItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.removeItem),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            concatMap(([action, state]) => {
                return of(
                    NavigationApiActions.saveNavigation({
                        application: state.application,
                        layout: this.navigationService.getLayout(action.group),
                        team: this.navigationService.getTeam(action.group),
                        sections: action.group.items,
                        section: action.section,
                    })
                );
            })
        );
    });

    removeItemFromGroups$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.removeItemFromGroups),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            mergeMap(([action, state]) => {
                const found = state.navigation[state.application].find(group => {
                    if (group.id === NavigationGroupTypes.System) return false;
                    return group.items.find(section => {
                        return section.items.find(item => {
                            return item.viewState.guid === action.vs.guid;
                        });
                    });
                });

                if (!found && action.delete) {
                    return of(ViewStateApiActions.deleteViewStateRecord({ vs: action.vs }));
                }

                return of(ApplicationActions.noOpAction());
            })
        );
    });

    storeNavigationData$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    NavigationActions.storeNavigationData,
                    NavigationActions.updateGroup,
                    NavigationActions.updateGroupState,
                    NavigationActions.updateSection,
                    NavigationActions.updateSectionState,
                    NavigationActions.addGroup,
                    NavigationActions.addSection,
                    NavigationActions.removeSection,
                    NavigationActions.enterSearchMode,
                    NavigationApiActions.createNavigationItemSuccess,
                    NavigationApiActions.addNavigationItemSuccess,
                    NavigationActions.removeItem,
                    NavigationActions.removeItemFromGroups,
                    NavigationApiActions.saveCurrentNavigationItemSuccess,
                    NavigationApiActions.updateNavigationItemSuccess,
                    NavigationActions.reorderItems
                ),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([_, state]) => {
                    localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(state));
                })
            );
        },
        { dispatch: false }
    );

    setCurrentItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.setCurrentItem),
            concatLatestFrom(() => this.store.select(selectCurrentItem)),
            mergeMap(([_, item]) => {
                const vs = item && item.viewState ? item.viewState : null;
                return of(WireApiActions.stopPolling({ restart: false }), ViewStateActions.setCurrentViewState({ vs: vs }));
            })
        );
    });

    reorderItems$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.reorderItems),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            map(([action, state]) => {
                return NavigationApiActions.saveNavigation({
                    application: state.application,
                    layout: this.navigationService.getLayout(action.group),
                    team: this.navigationService.getTeam(action.group),
                    sections: action.group.items,
                    section: null,
                });
            })
        );
    });

    constructor(
        private actions$: Actions,
        private store: Store,
        public router: Router,
        private navigationService: NavigationService,
        private viewStateService: LayoutState,
        private nomWebServicesJson: NomWebServicesJson,
        private globalTablesService: GlobalTablesService,
        public languageSettings: LanguageSettings
    ) {}

    private getNavigationCopy(sections: Array<NavigationSection>): any {
        return sections.map((section, index) => {
            return {
                Opened: section.opened,
                inEditMode: false,
                isNewSection: false,
                PrevOpened: false,
                ID: section.id,
                Label: section.label,
                Order: index,
                Hidden: section.hidden,
                Enterprise: section.enterprise,
                Items: section.items.map((item, idx) => {
                    return {
                        SectionID: item.sectionId,
                        Label: item.label,
                        Database: item.database,
                        Path: item.viewState.path.fullPath,
                        Guid: item.viewState.guid,
                        Hidden: item.hidden,
                        System: item.system,
                        ViewState: null,
                        SectionPath: item.sectionPath,
                        GroupPath: item.groupPath,
                        Url: null,
                        IsDefault: item.default,
                        Order: idx,
                    };
                }),
            };
        });
    }
    private createSystemSearchNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();

        // Create system search navigation item
        let vs: SearchViewStateRecord = null;
        let type: number = ViewStateTypes.SEARCH;
        if (app === 'playbook') {
            type = ViewStateTypes.SEARCH;
        }
        if (app === 'discover') {
            type = ViewStateTypes.WIRESEARCH;
        }
        if (app === 'journal') {
            type = ViewStateTypes.DRAFTSEARCH;
        }

        vs = RecordFactory.createViewStateForType<SearchViewStateRecord>(type);

        vs.guid = NavigationItemTypes.SYSTEM_SEARCH_GUID;
        vs.viewStateFilterState = true;
        vs.database = database;
        vs.path = new Path('');
        vs.viewStateSearchQuery = '';
        vs.title = 'Search';
        vs.viewStateFilter = ViewStateFilterSerializer.Instance(this.globalTablesService).initialize(type);
        vs.viewStateSorting = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(type, PSTFieldNames.PSTViewStateSorting);
        vs.viewStateGrouping = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(type, PSTFieldNames.PSTViewStateGrouping);

        const item: NavigationItem = {
            database: database,
            path: null,
            guid: vs.guid,
            hidden: false,
            label: 'Search',
            sectionPath: 'system',
            groupPath: 'system',
            sectionId: -1,
            viewState: vs,
            url: ['/PST', app, 'system', 'search', NavigationItemTypes.SYSTEM_SEARCH_GUID],

            // @TODO
            default: false,
            order: 1,
            system: false,
            enterprise: false,
        };

        return item;
    }

    private createSystemCurrentNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();
        const item: NavigationItem = {
            database: database,
            path: '',
            guid: NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID,
            hidden: false,
            label: 'Current',
            sectionPath: 'system',
            groupPath: 'system',
            sectionId: -1,
            viewState: null,
            url: ['/PST', app, 'system', 'current', NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID],

            // @TODO
            default: false,
            order: 2,
            system: false,
            enterprise: false,
        };

        return item;
    }

    private createSystemNewNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();
        const item: NavigationItem = {
            database: database,
            path: '',
            guid: NavigationItemTypes.SYSTEM_NEW_VIEW_GUID,
            hidden: false,
            label: 'New',
            sectionPath: 'system',
            groupPath: 'system',
            sectionId: -1,
            viewState: null,
            url: ['/PST', app, 'system', 'new', NavigationItemTypes.SYSTEM_NEW_VIEW_GUID],

            // @TODO
            default: false,
            order: 3,
            system: false,
            enterprise: false,
        };

        return item;
    }

    private createSystemSingleItemNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();
        const path = '';
        const vs: SingleItemViewStateRecord = RecordFactory.createViewStateForType<SingleItemViewStateRecord>(ViewStateTypes.SINGLEITEM);
        vs.guid = NavigationItemTypes.SYSTEM_SINGLE_ITEM_VIEW_GUID;
        vs.database = database;
        vs.path = new Path(path);
        vs.title = 'Single Item';
        vs.viewStateFilter = ViewStateFilterSerializer.Instance(this.globalTablesService).initialize(ViewStateTypes.SINGLEITEM);
        vs.viewStateSorting = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(ViewStateTypes.SINGLEITEM, PSTFieldNames.PSTViewStateSorting);
        vs.viewStateGrouping = ViewStateOptionsSerializer.Instance(this.globalTablesService).initialize(ViewStateTypes.SINGLEITEM, PSTFieldNames.PSTViewStateGrouping);

        const item: NavigationItem = {
            database: database,
            path: path,
            guid: vs.guid,
            hidden: false,
            label: 'Item',
            sectionPath: 'system',
            groupPath: 'system',
            sectionId: -1,
            viewState: vs,
            url: ['/PST', app, 'system', 'item', NavigationItemTypes.SYSTEM_SINGLE_ITEM_VIEW_GUID],
            default: false,
            order: 4,
            system: true,
            enterprise: false,
        };

        return item;
    }

    private createNewNavigationItem(app: string, group: NavigationGroup, section: NavigationSection, vs: ViewStateRecord): NavigationItem {
        const groupPath: string = group.label.replace(/\W+/g, '_').toLowerCase();
        const sectionPath: string = section.label.replace(/\W+/g, '_').toLowerCase();
        const database = this.nomWebServicesJson.getDatabase();
        const item: NavigationItem = {
            database: database,
            path: vs.fullPath,
            guid: vs.guid,
            hidden: false,
            label: vs.title,
            sectionPath: sectionPath,
            groupPath: groupPath,
            sectionId: section.id,
            viewState: vs,
            url: ['/PST', app, groupPath, sectionPath, vs.guid],

            default: false,
            order: -1,
            system: false,
            enterprise: true,
        };

        return item;
    }
}
